import { Registration, Webinar } from "./types";
import { formatLongDate, formatTime } from "./format";

// Prepares joining instructions for the administrator to send by hand. These helpers only
// build text and links: nothing here sends a message.

export const LINK_PLACEHOLDER = "[Insert Webinar Link]";

const messageBody = (webinar: Webinar, joiningLink: string) =>
  `thank you for registering for the KOLE MEDICAL webinar: ${webinar.title}. ` +
  `The session will take place on ${formatLongDate(webinar.startAt)} at ${formatTime(webinar.startAt)} (SAST). ` +
  `You can join using this link: ${joiningLink.trim() || LINK_PLACEHOLDER}. We look forward to having you join us.`;

export const joiningMessage = (registration: Registration, webinar: Webinar, joiningLink: string) =>
  `Hi ${registration.firstName}, ${messageBody(webinar, joiningLink)}`;

/** The same message without a name, for sending to several people at once */
export const groupJoiningMessage = (webinar: Webinar, joiningLink: string) => `Hello, ${messageBody(webinar, joiningLink)}`;

export const emailSubject = (webinar: Webinar) => `Joining instructions: ${webinar.title}`;

export const emailBody = (message: string) => `${message}\n\nKind regards,\nKOLE MEDICAL`;

/**
 * Phone number → the international digits wa.me expects, or null if it can't be worked out.
 * "082 123 4567" → "27821234567"; "+27 82 123 4567" → "27821234567".
 */
export function whatsAppNumber(phone: string): string | null {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10 && digits.startsWith("0")) return `27${digits.slice(1)}`;
  if (digits.length === 11 && digits.startsWith("27")) return digits;
  if (phone.trim().startsWith("+") && digits.length >= 10 && digits.length <= 15) return digits;
  return null;
}

/** Opens WhatsApp with the message filled in. The administrator still has to press send. */
export function whatsAppLink(phone: string, message: string): string | null {
  const number = whatsAppNumber(phone);
  return number ? `https://wa.me/${number}?text=${encodeURIComponent(message)}` : null;
}

/** Opens the default email app with the message filled in. Nothing is sent automatically. */
export const emailLink = (email: string, subject: string, body: string) =>
  `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

/** Some email apps won't open longer mailto links */
const MAILTO_LIMIT = 2000;

/**
 * One email with everyone in BCC, so nobody sees the others' addresses. null when the link
 * would be too long for email apps to open: copy the addresses instead.
 */
export function bccEmailLink(emails: string[], subject: string, body: string): string | null {
  const link = `mailto:?bcc=${emails.map(encodeURIComponent).join(",")}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return link.length <= MAILTO_LIMIT ? link : null;
}
